import React from "react";
import type { TrialAnalysisResult } from "~/types/api";
import MatchScoreBadge from "./MatchScoreBadge";
import { trackEvent } from "~/lib/analytics";
import { getCsrfToken } from "~/lib/api";

interface TrialResultsTeaserProps {
  result: TrialAnalysisResult;
  googleAuthPath: string;
}

const TrialResultsTeaser: React.FC<TrialResultsTeaserProps> = ({ result, googleAuthPath }) => {
  const csrfToken = getCsrfToken();
  const totalSkills = result.total_matched + result.total_critical_missing + result.total_nice_to_have;
  const isStrongMatch = result.match_score >= 75;
  const ctaHeadline = isStrongMatch
    ? "Strong match — unlock your full breakdown + salary range"
    : `You're missing ${result.total_critical_missing} critical skills — see which ones`;
  const buttonLabel = "Unlock full analysis — free";

  return (
    <div className="space-y-6">
      {/* Score + Summary */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <MatchScoreBadge score={result.match_score} />
        <div className="flex-1 pt-1">
          <p className="text-sm text-foreground leading-relaxed">{result.summary}</p>
        </div>
      </div>

      {/* Verdict banner */}
      {result.verdict && (() => {
        const v = result.verdict.toLowerCase();
        const isStrong = v.startsWith("apply — strong");
        const isApply = v.startsWith("apply");
        const bg = isStrong ? "bg-green-50" : isApply ? "bg-amber-50" : "bg-red-50";
        const border = isStrong ? "border-green-200" : isApply ? "border-amber-200" : "border-red-200";
        const text = isStrong ? "text-green-800" : isApply ? "text-amber-800" : "text-red-800";
        return (
          <div className={`rounded-lg border ${border} ${bg} px-4 py-3`}>
            <p className={`text-sm font-medium ${text}`}>{result.verdict}</p>
          </div>
        );
      })()}

      {/* Skills preview (visible) */}
      <div className="rounded-xl border bg-card p-5 space-y-4">
        <p className="text-sm font-semibold">Skills Preview</p>

        {result.matched_preview.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-2">Matched skills</p>
            <div className="flex flex-wrap gap-1.5">
              {result.matched_preview.map((skill) => (
                <span key={skill} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                  {skill}
                </span>
              ))}
              {result.total_matched > 3 && (
                <span className="text-xs text-muted-foreground px-2 py-0.5 italic">
                  +{result.total_matched - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {result.critical_missing_preview.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-2">Critical gaps</p>
            <div className="flex flex-wrap gap-1.5">
              {result.critical_missing_preview.map((skill) => (
                <span key={skill} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
                  {skill}
                </span>
              ))}
              {result.total_critical_missing > 3 && (
                <span className="text-xs text-muted-foreground px-2 py-0.5 italic">
                  +{result.total_critical_missing - 3} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Visual separator before locked section */}
      <div className="pt-2">
        <p className="text-sm font-semibold text-muted-foreground">Want the full picture?</p>
      </div>

      {/* Locked full results + CTA overlay */}
      <div className="relative rounded-xl overflow-hidden">
        {/* Blurred placeholder */}
        <div className="blur-sm pointer-events-none select-none rounded-xl border bg-card p-5 space-y-4 opacity-60">
          <p className="text-sm font-semibold">Full Skills Breakdown</p>
          {(result.nice_to_have_preview ?? []).length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-2">Nice-to-have gaps</p>
              <div className="flex flex-wrap gap-1.5">
                {(result.nice_to_have_preview ?? []).map((s) => (
                  <span key={s} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{s}</span>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="rounded-lg border p-3 space-y-1">
              <p className="text-xs font-medium">Company Intel</p>
              <p className="text-xs text-muted-foreground">Rating · Culture · Size</p>
            </div>
            <div className="rounded-lg border p-3 space-y-1">
              <p className="text-xs font-medium">Salary Data</p>
              <p className="text-xs text-muted-foreground">Market & company ranges</p>
            </div>
          </div>
        </div>

        {/* CTA overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/85 rounded-xl">
          <p className="text-base font-semibold text-center px-4">{ctaHeadline}</p>
          <p className="text-sm text-muted-foreground text-center mt-1 mb-5 px-6 max-w-xs">
            {totalSkills} skills analyzed · salary benchmarks · company intel
          </p>
          <form method="post" action={googleAuthPath}>
            <input type="hidden" name="authenticity_token" value={csrfToken} />
            <button
              type="submit"
              onClick={() => trackEvent("signup_cta_click", { source: "trial_results", score_tier: isStrongMatch ? "high" : "low" })}
              className="inline-flex items-center justify-center rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 text-sm font-semibold transition-colors cursor-pointer"
            >
              {buttonLabel}
            </button>
          </form>
          <p className="text-xs text-muted-foreground mt-3">Free during beta · No credit card</p>
        </div>
      </div>
    </div>
  );
};

export default TrialResultsTeaser;
